import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Box, Icon, PlainButton, Tooltip, TooltipContent, useSnackbars } from '@customerio/pluma-components/react';
import type { BoxProps } from '@customerio/pluma-components/react';
import { copyToClipboard } from '@/hooks/user-actions/use-copy-to-clipboard';
import * as styles from './styles.css';

/**
 * `content` and `onCopy` are also `div` attributes (RDFa `content`, the
 * `copy` clipboard handler), so they are dropped from the forwarded box
 * props rather than intersected with them.
 */
type CopyToClipboardProps = {
  /** The string to copy when the button is clicked. */
  content: string;
  /** The visible content the button hovers over. */
  children: ReactNode;
  /**
   * Snackbar id namespace to avoid colliding when multiple copy
   * actions live on the same page. Final id is
   * `copy.<idNamespace>.<success|error>`.
   */
  idNamespace?: string;
  /** Called after the content is successfully copied (e.g. analytics). */
  onCopy?: () => void;
  /**
   * Render a trigger of your own instead of the hover-revealed icon button —
   * Ember's `@showDefaultCopyButton={{false}}` slot. A custom trigger stays
   * visible rather than fading in on hover, matching the Ember callers.
   */
  renderTrigger?: (props: { copy: () => void }) => ReactNode;
} & Omit<BoxProps, 'content' | 'onCopy' | 'children' | 'as'>;

/**
 * Simplified port of the Ember `<CopyToClipboard>` component
 * (`~/code/ui/app/components/copy-to-clipboard/index.gts`).
 *
 * Wraps arbitrary content and reveals a small copy-icon button on
 * hover/focus, absolutely positioned over the content's right edge.
 * The button carries a soft `boxShadow` gradient feathered into the
 * surrounding surface so the button doesn't look pasted on top of
 * the content behind it.
 *
 * Supports Ember's slotted-button case through `renderTrigger`; still no
 * anchor mode or per-instance backgrounds — those can be added as needed
 * when callers ask for them.
 *
 * Accepts Pluma sprinkles (`flexGrow`, `maxW`, …), which are applied to the
 * wrapper, so a caller can size it inside its row without a stylesheet.
 */
export function CopyToClipboard({
  content,
  children,
  idNamespace = 'default',
  onCopy,
  className,
  renderTrigger,
  ...boxProps
}: CopyToClipboardProps) {
  const snackbars = useSnackbars();

  const handleClick = async () => {
    const ok = await copyToClipboard(content);
    snackbars.create({
      variant: ok ? 'default' : 'critical',
      id: `copy.${idNamespace}.${ok ? 'success' : 'error'}`,
      message: ok ? 'Copied to clipboard' : 'Could not copy to clipboard',
    });
    if (ok) onCopy?.();
  };

  return (
    <Box as="span" className={clsx(styles.wrapper, className)} {...boxProps}>
      {children}
      <span className={clsx(styles.buttonContainer, renderTrigger != null && styles.buttonContainerPersistent)}>
        {renderTrigger != null ? (
          renderTrigger({ copy: () => void handleClick() })
        ) : (
          <Tooltip isCustom>
            {(context) => (
              <>
                <PlainButton
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    void handleClick();
                  }}
                  aria-label="Copy to clipboard"
                  data-test-copy-button
                >
                  <Icon name="copy-to-clipboard" size="sm" />
                </PlainButton>
                <TooltipContent>Click to copy</TooltipContent>
              </>
            )}
          </Tooltip>
        )}
      </span>
    </Box>
  );
}
